/**
 * SuperAdmin Centres page
 * GET    /api/superadmin/tenants           — paginated roster with stats
 * PATCH  /api/superadmin/tenants/:id/plan  — plan override (reuses changeTenantPlan)
 * DELETE /api/superadmin/tenants/:id       — suspend (soft-delete)
 *
 * Access: superadminOnly — never expose to centre admins.
 * No amounts shown. Healer count only — no patient PII.
 */

import React, { useState, useEffect } from 'react';
import { getSuperAdminTenants, changeTenantPlan, deleteTenant, patchTenantConfig, unlockTenant, getSuperAdminOwners, addOwnerCentre, removeOwnerCentre, getCentreAdmins, addCentreAdmin, removeCentreAdmin, getSuperAdminUsers } from '../../utils/api';
import {
  FiX, FiChevronRight, FiRefreshCw, FiLoader, FiSearch,
  FiAlertTriangle, FiCheckCircle,
} from 'react-icons/fi';

const PLANS       = ['FREE', 'STARTER', 'AI_ADMIN', 'SCALE'];
const PLAN_LABELS = { FREE: 'Free', STARTER: 'Starter', AI_ADMIN: 'AI Admin', SCALE: 'Scale' };
const PLAN_COLORS = {
  FREE:     { bg: 'rgba(149,165,166,0.15)', color: '#7f8c8d' },
  STARTER:  { bg: 'rgba(52,152,219,0.12)',  color: '#2980b9' },
  AI_ADMIN: { bg: 'rgba(26,107,90,0.12)',   color: '#1a6b5a' },
  SCALE:    { bg: 'rgba(142,68,173,0.12)',  color: '#8e44ad' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { dateStyle: 'medium' });
}

function relativeTime(iso) {
  if (!iso) return 'Never';
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const STYLE_ID = 'sa-centres-anim';

// ─── Toast ──────────────────────────────────────────────────────────────────
const Toast = ({ type, message }) => (
  <div style={{
    position: 'fixed', top: '20px', right: '20px', zIndex: 600,
    background: type === 'success' ? '#27ae60' : '#e74c3c', color: '#fff',
    borderRadius: '8px', padding: '12px 18px',
    display: 'flex', alignItems: 'center', gap: '9px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)', fontSize: '13px', fontWeight: '600',
    animation: 'sa-fadein 0.2s ease', maxWidth: '320px',
  }}>
    {type === 'success'
      ? <FiCheckCircle style={{ flexShrink: 0 }} />
      : <FiAlertTriangle style={{ flexShrink: 0 }} />}
    {message}
  </div>
);

// ─── Confirm Modal ───────────────────────────────────────────────────────────
const ConfirmModal = ({ title, body, confirmLabel, confirmColor = '#e74c3c', loading, onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
    <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '420px', padding: '28px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
      <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>{title}</div>
      <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '24px' }}>{body}</div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} disabled={loading}
          style={{ padding: '10px 20px', borderRadius: '7px', border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>
          Cancel
        </button>
        <button type="button" onClick={onConfirm} disabled={loading}
          style={{ padding: '10px 20px', borderRadius: '7px', border: 'none', background: confirmColor, color: '#fff', fontSize: '13px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', minHeight: '44px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {loading && <FiLoader style={{ animation: 'sa-spin 1s linear infinite', fontSize: '13px' }} />}
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ─── Plan Badge ──────────────────────────────────────────────────────────────
const PlanBadge = ({ plan }) => {
  const c = PLAN_COLORS[plan] || { bg: '#f0f0f0', color: '#888' };
  return (
    <span style={{ fontSize: '10px', fontWeight: '700', borderRadius: '5px', padding: '2px 7px', background: c.bg, color: c.color, display: 'inline-block', whiteSpace: 'nowrap' }}>
      {PLAN_LABELS[plan] || plan}
    </span>
  );
};

// ─── Status Badge ────────────────────────────────────────────────────────────
const StatusBadge = ({ isActive }) => (
  <span style={{
    fontSize: '10px', fontWeight: '700', borderRadius: '5px', padding: '2px 7px',
    background: isActive ? 'rgba(39,174,96,0.1)' : 'rgba(231,76,60,0.08)',
    color:      isActive ? '#27ae60'              : '#e74c3c',
    display: 'inline-block', whiteSpace: 'nowrap',
  }}>
    {isActive ? 'Active' : 'Suspended'}
  </span>
);

// ─── Plan Override Modal ──────────────────────────────────────────────────────
const PlanOverrideModal = ({ tenant, onClose, onDone }) => {
  const [plan,    setPlan]    = useState(tenant.plan);
  const [reason,  setReason]  = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const handleSubmit = async () => {
    if (!reason.trim()) { setError('Reason is required.'); return; }
    setLoading(true); setError(null);
    try {
      await changeTenantPlan(tenant._id, plan, reason.trim());
      onDone(tenant._id, plan);
    } catch (err) {
      setError(err.response?.data?.message || 'Plan override failed.');
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '440px', padding: '28px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e' }}>Change Plan</div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '44px', minWidth: '44px' }}><FiX /></button>
        </div>

        <div style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          {tenant.tenantName} — current: <strong>{PLAN_LABELS[tenant.plan] || tenant.plan}</strong>
        </div>

        <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>New plan</label>
        <select value={plan} onChange={e => setPlan(e.target.value)}
          style={{ width: '100%', padding: '10px 12px', borderRadius: '7px', border: '1px solid #ddd', fontSize: '13px', color: '#333', marginBottom: '14px', boxSizing: 'border-box' }}>
          {PLANS.map(p => <option key={p} value={p}>{PLAN_LABELS[p]}</option>)}
        </select>

        <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>
          Reason <span style={{ color: '#e74c3c' }}>*</span>
        </label>
        <textarea value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Reason for plan change (required)…" rows={3}
          style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '7px', border: '1px solid #ddd', fontSize: '13px', color: '#333', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5', outline: 'none', marginBottom: error ? '6px' : '20px' }} />
        {error && <div style={{ fontSize: '12px', color: '#e74c3c', marginBottom: '14px' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={loading}
            style={{ padding: '10px 20px', borderRadius: '7px', border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={loading || !reason.trim()}
            style={{ padding: '10px 20px', borderRadius: '7px', border: 'none', background: (loading || !reason.trim()) ? '#aaa' : '#1a6b5a', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: (loading || !reason.trim()) ? 'not-allowed' : 'pointer', minHeight: '44px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {loading && <FiLoader style={{ animation: 'sa-spin 1s linear infinite', fontSize: '13px' }} />}
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Copyable Value ──────────────────────────────────────────────────────────
const CopyableValue = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    if (!value || value === '—') return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard permission denied on some browsers — copy is best-effort, no UI needed
    }
  };
  return (
    <span
      onClick={handleCopy}
      title="Click to copy"
      style={{ cursor: value && value !== '—' ? 'pointer' : 'default', fontSize: '13px', fontWeight: '600', color: copied ? '#1a6b5a' : '#333', letterSpacing: '0.5px' }}
    >
      {copied ? 'Copied!' : value}
    </span>
  );
};

// ─── Detail Panel ────────────────────────────────────────────────────────────
const DetailPanel = ({ tenant, onClose, onPlanOverride, onSuspend, onUnlock, unlockLoading, onToggleApproval, approvalToggleLoading, onToggleAffinity, affinityToggleLoading, allOwners, onAddOwner, onRemoveOwner, ownerOpLoading, centreAdmins, centreCandidates, onAddAdmin, onRemoveAdmin, adminOpLoading }) => {
  const [addOwnerId, setAddOwnerId] = useState('');
  const [addAdminId, setAddAdminId] = useState('');
  const tId             = String(tenant._id);
  const tenantOwners    = allOwners.filter(o => o.ownedTenants.some(t => String(t._id) === tId));
  const availableOwners = allOwners.filter(o => !o.ownedTenants.some(t => String(t._id) === tId));
  const availableAdmins = centreCandidates.filter(
    u => !['superadmin', 'owner', 'admin', 'centreAdmin'].includes(u.role)
      && !centreAdmins.some(a => String(a._id) === String(u._id))
  );
  return (
  <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.10)', overflow: 'hidden', marginTop: '16px' }}>
    <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a2e' }}>{tenant.tenantName}</div>
        <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
          {tenant.city}{tenant.address ? ` — ${tenant.address}` : ''}
        </div>
      </div>
      <PlanBadge plan={tenant.plan} />
      <StatusBadge isActive={tenant.isActive} />
      <button type="button" onClick={onClose} aria-label="Close detail"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '44px', minWidth: '44px' }}>
        <FiX />
      </button>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', padding: '16px 20px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
      <div>
        <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Centre code</div>
        <CopyableValue value={tenant.centreCode || '—'} />
      </div>
      {[
        ['WhatsApp',     tenant.whatsappNumber          || '—'],
        ['Email',        tenant.contactEmail             || '—'],
        ['Joined',       fmtDate(tenant.createdAt)            ],
        ['Last active',  relativeTime(tenant.lastActiveAt)    ],
        ['Healers',      String(tenant.healerCount)           ],
        ['Patients',     String(tenant.patientCount)          ],
        ['Active cases', String(tenant.activeRequestCount)    ],
      ].map(([label, val]) => (
        <div key={label}>
          <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
          <div style={{ fontSize: '13px', color: '#333', fontWeight: '500', marginTop: '2px' }}>{val}</div>
        </div>
      ))}
    </div>

    {/* Subscription state — shown when locked or in grace period */}
    {(tenant.subscriptionLocked || tenant.subscriptionGracePeriodEnd) && (
      <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Subscription
          </div>
          {tenant.subscriptionLocked
            ? <div style={{ fontSize: '11px', color: '#B00020', marginTop: '2px' }}>Access locked — payment overdue</div>
            : <div style={{ fontSize: '11px', color: '#856404', marginTop: '2px' }}>Grace period ends {fmtDate(tenant.subscriptionGracePeriodEnd)}</div>
          }
        </div>
        {tenant.subscriptionLocked
          ? <span style={{ fontSize: '11px', fontWeight: '700', borderRadius: '5px', padding: '2px 8px', background: 'rgba(176,0,32,0.08)', color: '#B00020', whiteSpace: 'nowrap' }}>LOCKED</span>
          : <span style={{ fontSize: '11px', fontWeight: '700', borderRadius: '5px', padding: '2px 8px', background: 'rgba(255,248,225,0.8)', color: '#856404', whiteSpace: 'nowrap' }}>GRACE</span>
        }
        {tenant.subscriptionLocked && (
          <button
            type="button"
            onClick={() => onUnlock(tenant)}
            disabled={unlockLoading === tenant._id}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #1a6b5a', background: '#fff', color: '#1a6b5a', fontSize: '12px', fontWeight: '600', cursor: unlockLoading === tenant._id ? 'not-allowed' : 'pointer', minHeight: '36px', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}
          >
            {unlockLoading === tenant._id && (
              <FiLoader style={{ animation: 'sa-spin 1s linear infinite', fontSize: '11px' }} />
            )}
            Unlock
          </button>
        )}
      </div>
    )}

    {/* Healer approval toggle */}
    <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', flex: 1 }}>
        Healer Approval
      </div>
      <span style={{
        fontSize: '11px', fontWeight: '700', borderRadius: '5px', padding: '2px 8px',
        background: tenant.requireHealerSuperAdminApproval ? 'rgba(26,107,90,0.1)' : 'rgba(231,76,60,0.08)',
        color: tenant.requireHealerSuperAdminApproval ? '#1a6b5a' : '#e74c3c',
        whiteSpace: 'nowrap',
      }}>
        {tenant.requireHealerSuperAdminApproval
          ? 'ON — all healers need SuperAdmin approval (default)'
          : 'OFF — centre admin approval is final'}
      </span>
      <button
        type="button"
        onClick={() => onToggleApproval(tenant._id, !tenant.requireHealerSuperAdminApproval)}
        disabled={approvalToggleLoading === tenant._id}
        style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: '12px', fontWeight: '600', cursor: approvalToggleLoading === tenant._id ? 'not-allowed' : 'pointer', minHeight: '36px', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}
      >
        {approvalToggleLoading === tenant._id && (
          <FiLoader style={{ animation: 'sa-spin 1s linear infinite', fontSize: '11px' }} />
        )}
        {tenant.requireHealerSuperAdminApproval ? 'Set to OFF' : 'Set to ON'}
      </button>
    </div>

    {/* AI Affinity Recommendations toggle */}
    <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          AI Affinity Recommendations
        </div>
        <div style={{ fontSize: '11px', color: '#bbb', marginTop: '2px' }}>Enable after 3+ months of session data</div>
      </div>
      <span style={{
        fontSize: '11px', fontWeight: '700', borderRadius: '5px', padding: '2px 8px',
        background: tenant.affinityRecommendationsEnabled ? 'rgba(26,107,90,0.1)' : 'rgba(149,165,166,0.12)',
        color: tenant.affinityRecommendationsEnabled ? '#1a6b5a' : '#7f8c8d',
        whiteSpace: 'nowrap',
      }}>
        {tenant.affinityRecommendationsEnabled ? 'ON' : 'OFF'}
      </span>
      <button
        type="button"
        onClick={() => onToggleAffinity(tenant._id, !tenant.affinityRecommendationsEnabled)}
        disabled={affinityToggleLoading === tenant._id}
        style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: '12px', fontWeight: '600', cursor: affinityToggleLoading === tenant._id ? 'not-allowed' : 'pointer', minHeight: '36px', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}
      >
        {affinityToggleLoading === tenant._id && (
          <FiLoader style={{ animation: 'sa-spin 1s linear infinite', fontSize: '11px' }} />
        )}
        {tenant.affinityRecommendationsEnabled ? 'Set to OFF' : 'Set to ON'}
      </button>
    </div>

    {/* Owners */}
    <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
        Owners
      </div>
      {tenantOwners.length === 0
        ? <div style={{ fontSize: '12px', color: '#bbb', marginBottom: '8px' }}>No owners assigned</div>
        : tenantOwners.map(owner => (
            <div key={owner._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <div style={{ flex: 1, fontSize: '13px', color: '#333' }}>
                {owner.firstName} {owner.lastName}
                <span style={{ color: '#888', fontSize: '11px', marginLeft: '6px' }}>{owner.email}</span>
              </div>
              <button
                type="button"
                disabled={ownerOpLoading === String(owner._id)}
                onClick={() => onRemoveOwner(owner._id, `${owner.firstName} ${owner.lastName}`)}
                style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #e74c3c', background: '#fff', color: '#e74c3c', fontSize: '11px', fontWeight: '600', cursor: ownerOpLoading === String(owner._id) ? 'not-allowed' : 'pointer', minHeight: '28px' }}
              >
                {ownerOpLoading === String(owner._id) ? '…' : 'Remove'}
              </button>
            </div>
          ))
      }
      {availableOwners.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <select
            value={addOwnerId}
            onChange={e => setAddOwnerId(e.target.value)}
            style={{ flex: 1, padding: '8px 10px', borderRadius: '7px', border: '1px solid #ddd', fontSize: '12px', color: '#333', minHeight: '36px' }}
          >
            <option value="">Add an owner…</option>
            {availableOwners.map(o => (
              <option key={o._id} value={o._id}>{o.firstName} {o.lastName} ({o.email})</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!addOwnerId || !!ownerOpLoading}
            onClick={() => { const id = addOwnerId; setAddOwnerId(''); onAddOwner(id); }}
            style={{ padding: '8px 14px', borderRadius: '7px', border: 'none', background: (addOwnerId && !ownerOpLoading) ? '#1a6b5a' : '#ccc', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: (addOwnerId && !ownerOpLoading) ? 'pointer' : 'default', minHeight: '36px', whiteSpace: 'nowrap' }}
          >
            Assign
          </button>
        </div>
      )}
    </div>

    {/* Admins */}
    <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
        Admins
      </div>
      {centreAdmins.length === 0
        ? <div style={{ fontSize: '12px', color: '#bbb', marginBottom: '8px' }}>No admins assigned</div>
        : centreAdmins.map(admin => (
            <div key={admin._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <div style={{ flex: 1, fontSize: '13px', color: '#333' }}>
                {admin.firstName} {admin.lastName}
                <span style={{ color: '#888', fontSize: '11px', marginLeft: '6px' }}>{admin.email}</span>
              </div>
              <button
                type="button"
                disabled={adminOpLoading === String(admin._id)}
                onClick={() => onRemoveAdmin(admin._id, `${admin.firstName} ${admin.lastName}`)}
                style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #e74c3c', background: '#fff', color: '#e74c3c', fontSize: '11px', fontWeight: '600', cursor: adminOpLoading === String(admin._id) ? 'not-allowed' : 'pointer', minHeight: '28px' }}
              >
                {adminOpLoading === String(admin._id) ? '…' : 'Remove'}
              </button>
            </div>
          ))
      }
      {availableAdmins.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <select
            value={addAdminId}
            onChange={e => setAddAdminId(e.target.value)}
            style={{ flex: 1, padding: '8px 10px', borderRadius: '7px', border: '1px solid #ddd', fontSize: '12px', color: '#333', minHeight: '36px' }}
          >
            <option value="">Add an admin…</option>
            {availableAdmins.map(u => (
              <option key={u._id} value={u._id}>{u.firstName} {u.lastName} ({u.email})</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!addAdminId || !!adminOpLoading}
            onClick={() => { const id = addAdminId; setAddAdminId(''); onAddAdmin(id); }}
            style={{ padding: '8px 14px', borderRadius: '7px', border: 'none', background: (addAdminId && !adminOpLoading) ? '#1a6b5a' : '#ccc', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: (addAdminId && !adminOpLoading) ? 'pointer' : 'default', minHeight: '36px', whiteSpace: 'nowrap' }}
          >
            Assign
          </button>
        </div>
      )}
    </div>

    <div style={{ padding: '16px 20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
      <button type="button" onClick={() => onPlanOverride(tenant)}
        style={{ padding: '10px 18px', borderRadius: '7px', border: 'none', background: '#1a6b5a', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
        Change Plan
      </button>
      {tenant.isActive && (
        <button type="button" onClick={() => onSuspend(tenant)}
          style={{ padding: '10px 18px', borderRadius: '7px', border: '1px solid #e74c3c', background: '#fff', color: '#e74c3c', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
          Suspend Centre
        </button>
      )}
    </div>
  </div>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  filterRow: { display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' },

  searchBox: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '7px 12px', borderRadius: '20px',
    border: '1px solid #e0e0e0', background: '#fff',
    fontSize: '12px', minHeight: '36px', flex: '0 0 200px',
  },

  chip: (active) => ({
    padding: '7px 14px', borderRadius: '20px', border: 'none',
    background: active ? '#1a6b5a' : '#fff',
    color:      active ? '#fff'    : '#555',
    fontSize: '12px', fontWeight: active ? '700' : '500',
    cursor: 'pointer', minHeight: '36px',
    boxShadow: active ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
    transition: 'background 0.15s, color 0.15s',
  }),

  tableWrap: { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: '8px' },

  tableHead: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 60px 70px 70px 80px 72px 36px',
    padding: '10px 16px', background: '#f8f9f8', borderBottom: '1px solid #eee',
    fontSize: '11px', fontWeight: '700', color: '#aaa',
    textTransform: 'uppercase', letterSpacing: '0.5px', gap: '8px',
  },

  tableRow: (selected) => ({
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 60px 70px 70px 80px 72px 36px',
    padding: '13px 16px', borderBottom: '1px solid #f6f6f6',
    cursor: 'pointer', gap: '8px', alignItems: 'center',
    background: selected ? 'rgba(26,107,90,0.05)' : '#fff',
    transition: 'background 0.12s', minHeight: '52px',
  }),

  cellPrimary:   { fontSize: '13px', fontWeight: '600', color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cellSecondary: { fontSize: '12px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cellNum:       { fontSize: '13px', fontWeight: '600', color: '#555', textAlign: 'center' },
  chevron:       { color: '#ccc', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
  empty:         { padding: '32px', textAlign: 'center', color: '#bbb', fontSize: '13px' },
};

// ─── Component ────────────────────────────────────────────────────────────────
const Centres = () => {
  const [tenants,        setTenants]        = useState([]);
  const [total,          setTotal]          = useState(0);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [selectedId,     setSelectedId]     = useState(null);
  const [planFilter,     setPlanFilter]     = useState('All');
  const [statusFilter,   setStatusFilter]   = useState('All');
  const [search,         setSearch]         = useState('');
  const [planOverride,   setPlanOverride]   = useState(null);  // tenant object
  const [suspendTarget,  setSuspendTarget]  = useState(null);  // tenant object
  const [suspendLoading,        setSuspendLoading]        = useState(false);
  const [approvalToggleLoading, setApprovalToggleLoading] = useState(null);
  const [affinityToggleLoading, setAffinityToggleLoading] = useState(null);
  const [toast,                 setToast]                 = useState(null);
  const [allOwners,             setAllOwners]             = useState([]);
  const [ownerOpLoading,        setOwnerOpLoading]        = useState(null);
  const [removeOwnerTarget,     setRemoveOwnerTarget]     = useState(null);
  const [centreAdminsMap,     setCentreAdminsMap]     = useState({});
  const [centreCandidatesMap, setCentreCandidatesMap] = useState({});
  const [adminOpLoading,      setAdminOpLoading]      = useState(null);
  const [removeAdminTarget,   setRemoveAdminTarget]   = useState(null);
  const [unlockLoading,       setUnlockLoading]       = useState(null);
  const [unlockTarget,        setUnlockTarget]        = useState(null);

  useEffect(() => {
    if (!document.getElementById(STYLE_ID)) {
      const el = document.createElement('style');
      el.id = STYLE_ID;
      el.textContent = `
        @keyframes sa-spin   { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
        @keyframes sa-fadein { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
      `;
      document.head.appendChild(el);
    }
  }, []);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchTenants = async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (planFilter !== 'All')           params.plan   = planFilter;
      if (statusFilter === 'Active')      params.status = 'active';
      if (statusFilter === 'Suspended')   params.status = 'suspended';
      if (search.trim())                  params.search = search.trim();
      const res = await getSuperAdminTenants(params);
      setTenants(res.data.data || []);
      setTotal(res.data.meta?.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load centres. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when plan or status filter chip changes; search fires on Enter.
  useEffect(() => { fetchTenants(); }, [planFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchKeyDown = (e) => { if (e.key === 'Enter') fetchTenants(); };

  const handleRowClick = (id) => setSelectedId(prev => prev === id ? null : id);

  const selectedTenant = tenants.find(t => t._id === selectedId) || null;

  const handlePlanDone = (tenantId, newPlan) => {
    setTenants(prev => prev.map(t => t._id === tenantId ? { ...t, plan: newPlan } : t));
    setPlanOverride(null);
    showToast('success', 'Plan updated successfully');
  };

  const handleToggleApproval = async (tenantId, newValue) => {
    if (approvalToggleLoading === tenantId) return;
    setApprovalToggleLoading(tenantId);
    try {
      await patchTenantConfig(tenantId, { requireHealerSuperAdminApproval: newValue });
      setTenants(prev => prev.map(t => t._id === tenantId ? { ...t, requireHealerSuperAdminApproval: newValue } : t));
      const name = tenants.find(t => t._id === tenantId)?.tenantName || 'Centre';
      showToast('success', `${name} — healer approval ${newValue ? 'set to ON' : 'set to OFF'}`);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Config update failed.');
    } finally {
      setApprovalToggleLoading(null);
    }
  };

  const handleToggleAffinity = async (tenantId, newValue) => {
    if (affinityToggleLoading === tenantId) return;
    setAffinityToggleLoading(tenantId);
    try {
      await patchTenantConfig(tenantId, { affinityRecommendationsEnabled: newValue });
      setTenants(prev => prev.map(t => t._id === tenantId ? { ...t, affinityRecommendationsEnabled: newValue } : t));
      const name = tenants.find(t => t._id === tenantId)?.tenantName || 'Centre';
      showToast('success', `${name} — AI affinity ${newValue ? 'enabled' : 'disabled'}`);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Config update failed.');
    } finally {
      setAffinityToggleLoading(null);
    }
  };

  const handleSuspendConfirm = async () => {
    if (!suspendTarget) return;
    setSuspendLoading(true);
    try {
      await deleteTenant(suspendTarget._id);
      setTenants(prev => prev.filter(t => t._id !== suspendTarget._id));
      setSelectedId(null);
      setSuspendTarget(null);
      showToast('success', `${suspendTarget.tenantName} has been suspended`);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Suspend failed. Please try again.');
    } finally {
      setSuspendLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getSuperAdminOwners();
        setAllOwners(res.data.data || []);
      } catch (_err) { /* non-fatal — owners section shows empty */ }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const tId = String(selectedId);
    if (centreAdminsMap[tId] !== undefined) return;
    (async () => {
      try {
        const [adminsRes, usersRes] = await Promise.allSettled([
          getCentreAdmins(selectedId),
          getSuperAdminUsers({ tenantId: selectedId, limit: 100 }),
        ]);
        setCentreAdminsMap(prev => ({ ...prev, [tId]: adminsRes.status === 'fulfilled' ? (adminsRes.value.data.data || []) : [] }));
        setCentreCandidatesMap(prev => ({ ...prev, [tId]: usersRes.status === 'fulfilled' ? (usersRes.value.data.users || []) : [] }));
      } catch (_) { /* non-fatal */ }
    })();
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddOwner = async (ownerId) => {
    if (!selectedId || ownerOpLoading) return;
    setOwnerOpLoading(ownerId);
    try {
      await addOwnerCentre(ownerId, selectedId);
      const t = tenants.find(ten => String(ten._id) === String(selectedId));
      setAllOwners(prev => prev.map(o =>
        String(o._id) === String(ownerId)
          ? { ...o, ownedTenants: [...o.ownedTenants, { _id: selectedId, tenantName: t?.tenantName, city: t?.city || null, isActive: true }] }
          : o
      ));
      showToast('success', `Owner assigned to ${t?.tenantName || 'centre'}`);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Assignment failed.');
    } finally {
      setOwnerOpLoading(null);
    }
  };

  const handleRemoveOwnerRequest = (ownerId, ownerName) => {
    if (!selectedId || ownerOpLoading) return;
    setRemoveOwnerTarget({ ownerId, ownerName });
  };

  const handleRemoveOwnerConfirm = async () => {
    const { ownerId } = removeOwnerTarget;
    setOwnerOpLoading(ownerId);
    try {
      await removeOwnerCentre(ownerId, selectedId);
      setAllOwners(prev => prev.map(o =>
        String(o._id) === String(ownerId)
          ? { ...o, ownedTenants: o.ownedTenants.filter(t => String(t._id) !== String(selectedId)) }
          : o
      ));
      showToast('success', 'Owner removed from centre');
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Remove failed.');
    } finally {
      setOwnerOpLoading(null);
      setRemoveOwnerTarget(null);
    }
  };

  const handleAddAdmin = async (userId) => {
    if (!selectedId || adminOpLoading) return;
    setAdminOpLoading(userId);
    const tId = String(selectedId);
    try {
      await addCentreAdmin(selectedId, userId);
      const candidate = (centreCandidatesMap[tId] || []).find(u => String(u._id) === String(userId));
      const shaped = candidate ? { _id: candidate._id, firstName: candidate.firstName, lastName: candidate.lastName, email: candidate.email, role: candidate.role } : null;
      setCentreAdminsMap(prev => ({ ...prev, [tId]: [...(prev[tId] || []), shaped].filter(Boolean) }));
      showToast('success', `${candidate?.firstName || 'User'} assigned as admin`);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Assignment failed.');
    } finally {
      setAdminOpLoading(null);
    }
  };

  const handleRemoveAdminRequest = (userId, adminName) => {
    if (!selectedId || adminOpLoading) return;
    setRemoveAdminTarget({ userId, adminName });
  };

  const handleRemoveAdminConfirm = async () => {
    const { userId } = removeAdminTarget;
    setAdminOpLoading(userId);
    const tId = String(selectedId);
    try {
      await removeCentreAdmin(selectedId, userId);
      setCentreAdminsMap(prev => ({ ...prev, [tId]: (prev[tId] || []).filter(a => String(a._id) !== String(userId)) }));
      showToast('success', 'Admin removed from centre');
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Remove failed.');
    } finally {
      setAdminOpLoading(null);
      setRemoveAdminTarget(null);
    }
  };

  const handleUnlockRequest = (tenant) => setUnlockTarget(tenant);

  const handleUnlockConfirm = async () => {
    if (!unlockTarget) return;
    setUnlockLoading(unlockTarget._id);
    try {
      await unlockTenant(unlockTarget._id);
      setTenants(prev => prev.map(t =>
        t._id === unlockTarget._id
          ? { ...t, subscriptionLocked: false, subscriptionGracePeriodEnd: null }
          : t
      ));
      showToast('success', `${unlockTarget.tenantName} — subscription unlocked`);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Unlock failed. Please try again.');
    } finally {
      setUnlockLoading(null);
      setUnlockTarget(null);
    }
  };

  return (
    <div>
      {toast && <Toast type={toast.type} message={toast.message} />}
      {planOverride && (
        <PlanOverrideModal tenant={planOverride} onClose={() => setPlanOverride(null)} onDone={handlePlanDone} />
      )}
      {suspendTarget && (
        <ConfirmModal
          title="Suspend centre?"
          body={`This will suspend ${suspendTarget.tenantName} immediately. The centre and all its data are preserved — this can be reversed by LifeChiAI support.`}
          confirmLabel="Suspend"
          confirmColor="#e74c3c"
          loading={suspendLoading}
          onConfirm={handleSuspendConfirm}
          onCancel={() => setSuspendTarget(null)}
        />
      )}
      {removeOwnerTarget && (
        <ConfirmModal
          title="Remove owner?"
          body={`Remove ${removeOwnerTarget.ownerName} as an owner of this centre? They will lose access to all owner-level features for this centre.`}
          confirmLabel="Remove"
          confirmColor="#e74c3c"
          loading={!!ownerOpLoading}
          onConfirm={handleRemoveOwnerConfirm}
          onCancel={() => setRemoveOwnerTarget(null)}
        />
      )}
      {removeAdminTarget && (
        <ConfirmModal
          title="Remove admin?"
          body={`Remove ${removeAdminTarget.adminName} as an admin of this centre? They will lose admin access but remain a member of the centre.`}
          confirmLabel="Remove"
          confirmColor="#e74c3c"
          loading={!!adminOpLoading}
          onConfirm={handleRemoveAdminConfirm}
          onCancel={() => setRemoveAdminTarget(null)}
        />
      )}
      {unlockTarget && (
        <ConfirmModal
          title="Unlock subscription?"
          body={`This will clear the subscription lock on ${unlockTarget.tenantName} and restore full access. Only do this after confirming payment has been received.`}
          confirmLabel="Unlock"
          confirmColor="#1a6b5a"
          loading={!!unlockLoading}
          onConfirm={handleUnlockConfirm}
          onCancel={() => setUnlockTarget(null)}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <div style={{ fontSize: '18px', fontWeight: '800', color: '#1a1a2e' }}>Centres</div>
        {!loading && (
          <span style={{ fontSize: '11px', fontWeight: '700', background: 'rgba(26,107,90,0.1)', color: '#1a6b5a', borderRadius: '12px', padding: '3px 10px' }}>
            {total}
          </span>
        )}
      </div>

      {/* Filter bar — plan chips */}
      <div style={S.filterRow}>
        <div style={S.searchBox}>
          <FiSearch style={{ color: '#bbb', flexShrink: 0 }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search name or city… (Enter)"
            style={{ border: 'none', outline: 'none', fontSize: '12px', color: '#333', background: 'transparent', width: '100%' }}
          />
        </div>
        {['All', ...PLANS].map(p => (
          <button key={p} type="button" style={S.chip(planFilter === p)}
            onClick={() => { setPlanFilter(p); setSelectedId(null); }}>
            {p === 'All' ? 'All plans' : (PLAN_LABELS[p] || p)}
          </button>
        ))}
      </div>

      {/* Filter bar — status chips + refresh */}
      <div style={{ ...S.filterRow, marginBottom: '16px' }}>
        {['All', 'Active', 'Suspended'].map(s => (
          <button key={s} type="button" style={S.chip(statusFilter === s)}
            onClick={() => { setStatusFilter(s); setSelectedId(null); }}>
            {s}
          </button>
        ))}
        <button type="button" onClick={fetchTenants}
          style={{ ...S.chip(false), display: 'flex', alignItems: 'center', gap: '5px' }}>
          <FiRefreshCw style={{ fontSize: '12px' }} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        <div style={S.tableHead}>
          <span>Centre</span>
          <span>City</span>
          <span style={{ textAlign: 'center' }}>Healers</span>
          <span style={{ textAlign: 'center' }}>Patients</span>
          <span style={{ textAlign: 'center' }}>Active</span>
          <span>Plan</span>
          <span>Status</span>
          <span />
        </div>

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '160px', gap: '10px', color: '#888' }}>
            <FiLoader style={{ fontSize: '18px', animation: 'sa-spin 1s linear infinite' }} />
            <span style={{ fontSize: '13px' }}>Loading centres…</span>
          </div>
        )}

        {!loading && error && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '160px', justifyContent: 'center', gap: '12px', padding: '24px', textAlign: 'center' }}>
            <div style={{ fontSize: '13px', color: '#e74c3c', maxWidth: '300px', lineHeight: '1.5' }}>{error}</div>
            <button type="button" onClick={fetchTenants}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '7px', border: 'none', background: '#1a6b5a', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
              <FiRefreshCw style={{ fontSize: '12px' }} /> Retry
            </button>
          </div>
        )}

        {!loading && !error && tenants.length === 0 && (
          <div style={S.empty}>No centres found.</div>
        )}

        {!loading && !error && tenants.map(t => (
          <div
            key={t._id}
            style={S.tableRow(selectedId === t._id)}
            onClick={() => handleRowClick(t._id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleRowClick(t._id)}
          >
            <div style={{ minWidth: 0 }}>
              <div style={S.cellPrimary} title={t.tenantName}>{t.tenantName}</div>
              {t.subscriptionLocked && (
                <div style={{ fontSize: '10px', color: '#B00020', fontWeight: '700', marginTop: '2px' }}>LOCKED</div>
              )}
            </div>
            <div style={S.cellSecondary}>{t.city || '—'}</div>
            <div style={S.cellNum}>{t.healerCount}</div>
            <div style={S.cellNum}>{t.patientCount}</div>
            <div style={S.cellNum}>{t.activeRequestCount}</div>
            <div><PlanBadge plan={t.plan} /></div>
            <div><StatusBadge isActive={t.isActive} /></div>
            <div style={S.chevron}><FiChevronRight /></div>
          </div>
        ))}
      </div>

      {/* Detail panel */}
      {selectedTenant && (
        <DetailPanel
          tenant={selectedTenant}
          onClose={() => setSelectedId(null)}
          onPlanOverride={setPlanOverride}
          onSuspend={setSuspendTarget}
          onUnlock={handleUnlockRequest}
          unlockLoading={unlockLoading}
          onToggleApproval={handleToggleApproval}
          approvalToggleLoading={approvalToggleLoading}
          onToggleAffinity={handleToggleAffinity}
          affinityToggleLoading={affinityToggleLoading}
          allOwners={allOwners}
          onAddOwner={handleAddOwner}
          onRemoveOwner={handleRemoveOwnerRequest}
          ownerOpLoading={ownerOpLoading}
          centreAdmins={centreAdminsMap[String(selectedId)] || []}
          centreCandidates={centreCandidatesMap[String(selectedId)] || []}
          onAddAdmin={handleAddAdmin}
          onRemoveAdmin={handleRemoveAdminRequest}
          adminOpLoading={adminOpLoading}
        />
      )}
    </div>
  );
};

export default Centres;
